import { c as _c } from "react/compiler-runtime";
import type { CountryRecord } from '@onefootprint/core';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryFromCode, getDefaultCountry } from '@onefootprint/core';
import { HeaderTitle, NavigationHeader, StickyBottomBox } from '@onefootprint/idv2';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type { IdDocOutcome, SubmitDocTypeResponse } from '@onefootprint/types';
import { SupportedIdDocTypes } from '@onefootprint/types/src/data/id-doc-type';
import type { RadioSelectOptionFields } from '@onefootprint/ui';
import { Button, Divider, Form, LinkButton, RadioSelect } from '@onefootprint/ui';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useL10nContext } from '@/idv/components/l10n-provider';
import { type DeviceInfo, useIdvRequestErrorToast, useRenderCountWarning } from '@/idv/hooks';
import { useCountryName } from '@/idv/hooks/use-country-name';
import { getLogger } from '@/idv/utils';
import { setIdv2StoreData } from '@/idv2/features/idv-to-idv2/idv2-provider';
import { markContinueOnMobile } from '@/idv2/index';
import useSubmitDocType from '../../../../hooks/use-submit-doc-type';
import { isMobileKind } from '../../../../utils/capture';
import detectWebcam from '../../../utils/detect-webcam';
import useOptionsByDocType from '../hooks/use-options-by-doc-type';
type FormData = {
  country: Iso3166TwoDigitCountryCode;
  docType: `${SupportedIdDocTypes}`;
};
type IdDocCountryAndTypeContainerProps = {
  onSubmitDocTypeSuccess: (data: SubmitDocTypeResponse, country: CountryRecord, docType: `${SupportedIdDocTypes}`) => void;
  userCountryCode?: Iso3166TwoDigitCountryCode;
  authToken: string;
  device: DeviceInfo;
  supportedCountryAndDocTypes: Partial<Record<Iso3166TwoDigitCountryCode, SupportedIdDocTypes[]>>;
  sandboxOutcome?: IdDocOutcome;
  selectedCountryCode?: Iso3166TwoDigitCountryCode;
  selectedDocType?: `${SupportedIdDocTypes}`;
  documentRequestId: string;
};
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'id-doc-country-and-type-container'
});
const getRecordKeys = <T extends object,>(object: T) => Object.keys(object) as (keyof T)[];
const sortDocTypes = (types: SupportedIdDocTypes[]) => {
  // rearrange supportedDocumentTypes to make sure the first three are drivers license, passport, id card if they are supported
  types.sort((a, b) => {
    const order = [SupportedIdDocTypes.driversLicense, SupportedIdDocTypes.passport, SupportedIdDocTypes.idCard];
    if (order.includes(a) && order.includes(b)) {
      return order.indexOf(a) - order.indexOf(b);
    }
    if (order.includes(a)) {
      return -1;
    }
    if (order.includes(b)) {
      return 1;
    }
    return 0;
  });
};
const IdDocCountryAndTypeContainer = t0 => {
  const $ = _c(86);
  const {
    onSubmitDocTypeSuccess,
    userCountryCode,
    selectedCountryCode,
    selectedDocType,
    authToken,
    device,
    supportedCountryAndDocTypes,
    sandboxOutcome,
    documentRequestId
  } = t0;
  useRenderCountWarning("id-doc-country-and-type-container");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.id-doc.pages.country-and-type-selection"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t1);
  const getCountryName = useCountryName();
  const requestErrorToast = useIdvRequestErrorToast();
  const submitDocTypeMutation = useSubmitDocType();
  const {
    isPending: isDocTypeSubmissionLoading
  } = submitDocTypeMutation;
  const l10n = useL10nContext();
  const {
    type: deviceType
  } = device;
  let supportedCountryRecords;
  let t2;
  if ($[1] !== selectedCountryCode || $[2] !== supportedCountryAndDocTypes || $[3] !== userCountryCode) {
    const supportedCountries = new Set(getRecordKeys(supportedCountryAndDocTypes));
    supportedCountryRecords = COUNTRIES.filter(country => supportedCountries.has(country.value));
    t2 = getDefaultCountry(supportedCountries, selectedCountryCode || userCountryCode);
    $[1] = selectedCountryCode;
    $[2] = supportedCountryAndDocTypes;
    $[3] = userCountryCode;
    $[4] = supportedCountryRecords;
    $[5] = t2;
  } else {
    supportedCountryRecords = $[4];
    t2 = $[5];
  }
  const defaultSupportedCountry = t2;
  const defaultCountryCode = defaultSupportedCountry?.value ?? DEFAULT_COUNTRY.value;
  let types;
  if ($[6] !== defaultCountryCode || $[7] !== supportedCountryAndDocTypes) {
    types = [...(supportedCountryAndDocTypes[defaultCountryCode] ?? [])];
    sortDocTypes(types);
    $[6] = defaultCountryCode;
    $[7] = supportedCountryAndDocTypes;
    $[8] = types;
  } else {
    types = $[8];
  }
  const firstTypeFromOptions = types.length ? types[0] : SupportedIdDocTypes.passport;
  const t3 = selectedDocType ?? firstTypeFromOptions;
  let t4;
  if ($[9] !== defaultCountryCode || $[10] !== t3) {
    t4 = {
      defaultValues: {
        country: defaultCountryCode,
        docType: t3
      }
    };
    $[9] = defaultCountryCode;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const formMethods = useForm(t4);
  const {
    setValue,
    handleSubmit,
    control
  } = formMethods;
  let t5;
  if ($[12] !== control) {
    t5 = {
      control,
      name: "country"
    };
    $[12] = control;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const watchedCountry = useWatch(t5);
  let t6;
  if ($[14] !== control) {
    t6 = {
      control,
      name: "docType"
    };
    $[14] = control;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const watchedDocType = useWatch(t6);
  let t7;
  if ($[16] !== setValue || $[17] !== supportedCountryAndDocTypes) {
    t7 = countryCode => {
      const typesForNextCountry = [...(supportedCountryAndDocTypes[countryCode] ?? [])];
      sortDocTypes(typesForNextCountry);
      const nextType = typesForNextCountry.length ? typesForNextCountry[0] : SupportedIdDocTypes.passport;
      setValue("docType", nextType);
    };
    $[16] = setValue;
    $[17] = supportedCountryAndDocTypes;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleCountryChange = t7;
  let t8;
  if ($[19] !== setValue) {
    t8 = value => {
      setValue("docType", value as SupportedIdDocTypes);
    };
    $[19] = setValue;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleDocTypeChange = t8;
  let t9;
  if ($[21] !== authToken || $[22] !== deviceType || $[23] !== documentRequestId || $[24] !== onSubmitDocTypeSuccess || $[25] !== requestErrorToast || $[26] !== sandboxOutcome || $[27] !== submitDocTypeMutation) {
    t9 = async data => {
      const hasWebcam = await detectWebcam();
      if (submitDocTypeMutation.isPending) {
        return;
      }
      const countryRecord = getCountryFromCode(data.country);
      if (!countryRecord) {
        logWarn(`Invalid country code: ${data.country}`);
        return;
      }
      logInfo(`submitting doc type - ${data.docType} and country - ${data.country}, hasWebcam: ${hasWebcam}`);
      submitDocTypeMutation.mutate({
        authToken,
        documentType: data.docType,
        countryCode: data.country,
        fixtureResult: sandboxOutcome,
        deviceType: isMobileKind(deviceType) ? "mobile" : "desktop",
        skipSelfie: !hasWebcam,
        requestId: documentRequestId
      }, {
        onSuccess: responseData => {
          onSubmitDocTypeSuccess(responseData, countryRecord, data.docType);
        },
        onError: err => {
          logWarn(`Failed to submit doc type and country. Selected doctype: ${data.docType}, country ${data.country}. Error:`, err);
          requestErrorToast(err);
        }
      });
    };
    $[21] = authToken;
    $[22] = deviceType;
    $[23] = documentRequestId;
    $[24] = onSubmitDocTypeSuccess;
    $[25] = requestErrorToast;
    $[26] = sandboxOutcome;
    $[27] = submitDocTypeMutation;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const onSubmit = t9;
  let currentTypes;
  if ($[29] !== supportedCountryAndDocTypes || $[30] !== watchedCountry) {
    currentTypes = [...(supportedCountryAndDocTypes[watchedCountry] ?? [])];
    sortDocTypes(currentTypes);
    $[29] = supportedCountryAndDocTypes;
    $[30] = watchedCountry;
    $[31] = currentTypes;
  } else {
    currentTypes = $[31];
  }
  const optionsByDocType = useOptionsByDocType(currentTypes);
  let t10;
  if ($[32] !== currentTypes || $[33] !== optionsByDocType) {
    t10 = currentTypes.map(type => optionsByDocType[type]).filter(_temp);
    $[32] = currentTypes;
    $[33] = optionsByDocType;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const options = t10;
  const handleContinueOnMobile = _temp2;
  const t11 = `flex flex-col gap-6 justify-center items-center ${isMobileKind(device.type) ? "-mt-4" : ""}`;
  let t12;
  if ($[35] !== device.type) {
    t12 = isMobileKind(device.type) ? undefined : {
      variant: "close",
      confirmClose: true
    };
    $[35] = device.type;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12) {
    t13 = <NavigationHeader leftButton={t12} />;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t) {
    t14 = t(_temp3);
    $[39] = t;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t(_temp4);
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t14 || $[44] !== t15) {
    t16 = <HeaderTitle title={t14} subtitle={t15} />;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t) {
    t17 = t(_temp5);
    $[46] = t;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const t18 = supportedCountryRecords.length <= 1;
  let t19;
  if ($[48] !== getCountryName || $[49] !== supportedCountryRecords.length || $[50] !== t || $[51] !== watchedCountry) {
    t19 = supportedCountryRecords.length === 1 ? t(_temp6, {
      country: getCountryName(watchedCountry)
    }) : undefined;
    $[48] = getCountryName;
    $[49] = supportedCountryRecords.length;
    $[50] = t;
    $[51] = watchedCountry;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  const t20 = l10n?.locale;
  let t21;
  if ($[53] !== handleCountryChange || $[54] !== supportedCountryRecords || $[55] !== t17 || $[56] !== t18 || $[57] !== t19 || $[58] !== t20) {
    t21 = <Form.Country name="country" label={t17} disabled={t18} options={supportedCountryRecords} onChange={handleCountryChange} hint={t19} testID="country-selector" locale={t20} />;
    $[53] = handleCountryChange;
    $[54] = supportedCountryRecords;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Divider />;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== handleDocTypeChange || $[62] !== options || $[63] !== optionsByDocType || $[64] !== t || $[65] !== watchedDocType) {
    t23 = options.length > 0 ? <RadioSelect value={optionsByDocType[watchedDocType]?.value} options={options} onChange={handleDocTypeChange} testID="doc-selector" size="compact" /> : <div className="text-body-2 text-primary text-center mx-4">{t(_temp7)}</div>;
    $[61] = handleDocTypeChange;
    $[62] = options;
    $[63] = optionsByDocType;
    $[64] = t;
    $[65] = watchedDocType;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== t21 || $[68] !== t23) {
    t24 = <div className="flex flex-col gap-4 justify-center items-center w-full [&>div]:w-full">{t21}{t22}{t23}</div>;
    $[67] = t21;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== device.type || $[71] !== handleSubmit || $[72] !== isDocTypeSubmissionLoading || $[73] !== onSubmit || $[74] !== options.length || $[75] !== t) {
    t25 = options.length > 0 && <StickyBottomBox><form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3 items-center"><Button type="submit" fullWidth={true} loading={isDocTypeSubmissionLoading} size="large" data-dd-action-name="country-doc:continue">{t(_temp8)}</Button>{!isMobileKind(device.type) && <LinkButton onClick={handleContinueOnMobile}>{t(_temp9)}</LinkButton>}</form></StickyBottomBox>;
    $[70] = device.type;
    $[71] = handleSubmit;
    $[72] = isDocTypeSubmissionLoading;
    $[73] = onSubmit;
    $[74] = options.length;
    $[75] = t;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== t11 || $[78] !== t13 || $[79] !== t16 || $[80] !== t24 || $[81] !== t25) {
    t26 = <div className={t11}>{t13}{t16}{t24}{t25}</div>;
    $[77] = t11;
    $[78] = t13;
    $[79] = t16;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== formMethods || $[84] !== t26) {
    t27 = <FormProvider {...formMethods}>{t26}</FormProvider>;
    $[83] = formMethods;
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  return t27;
};
export default IdDocCountryAndTypeContainer;
function _temp(option) {
  return !!option;
}
function _temp2() {
  markContinueOnMobile();
  setIdv2StoreData({
    relayToIDV2: true
  });
}
function _temp3($) {
  return $.title;
}
function _temp4($_0) {
  return $_0.subtitle;
}
function _temp5($_1) {
  return $_1.form.country;
}
function _temp6($_2) {
  return $_2.form.hint.label;
}
function _temp7($_3) {
  return $_3.form["not-supported"];
}
function _temp8($_4) {
  return $_4.form.cta;
}
function _temp9($_5) {
  return $_5.form["continue-on-mobile"];
}